import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Camera, Scale } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import { Card, Chip, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';

interface PhotoGuideCard {
  id: string;
  code: string;
  category: string;
  title: string;
  content: string;
  tips: string[];
}

/**
 * 활동사진 촬영 가이드 — 명세서 §5.24. 교사 전용 팁 카드 라이브러리.
 * 콘텐츠는 시드(§14.2-7)로 관리하므로 화면은 보여 주기만 한다.
 */
export function PhotoGuidePage() {
  const [category, setCategory] = useState('');

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['photo-guides'],
    queryFn: () => apiData<PhotoGuideCard[]>('/photo-guides'),
    staleTime: 30 * 60_000, // 시드 관리 정적 콘텐츠
  });

  const categories = [...new Set((data ?? []).map((card) => card.category))];
  const cards = (data ?? []).filter((card) => !category || card.category === category);

  return (
    <Screen>
      <AppBar title="촬영 가이드" back />

      <div className="flex flex-col gap-3 p-4">
        <p className="flex items-center gap-1.5 rounded-sm bg-info-soft px-3 py-2 text-sm">
          <Camera size={15} /> 사진 공개에 동의하지 않은 아이가 담기지 않게 늘 확인해 주세요.
        </p>

        {/* 촬영 밸런스 (§5.24) — "A는 찍었나? B는 덜 찍혔나?"를 세는 대신 여기서 본다 */}
        <CoverageCard />

        {categories.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            <Chip tone={category === '' ? 'primary' : 'neutral'} selected={category === ''} onClick={() => setCategory('')}>
              전체
            </Chip>
            {categories.map((item) => (
              <Chip
                key={item}
                tone={category === item ? 'primary' : 'neutral'}
                selected={category === item}
                onClick={() => setCategory(item)}
              >
                {item}
              </Chip>
            ))}
          </div>
        )}

        {isPending && <Spinner />}
        {isError && <ErrorNote message="가이드를 불러오지 못했어요." onRetry={() => void refetch()} />}

        {cards.map((card) => (
          <Card key={card.id}>
            <div className="mb-1 flex items-center gap-1.5">
              <Chip tone="primary">{card.category}</Chip>
            </div>
            <p className="font-semibold">{card.title}</p>
            <p className="mt-1 text-base text-ink-sub">{card.content}</p>
            {card.tips.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1 text-sm">
                {card.tips.map((tip) => (
                  <li key={tip}>· {tip}</li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>
    </Screen>
  );
}

interface CoverageRow {
  id: string;
  name: string;
  count: number;
  lastPhotoAt: string | null;
}

/**
 * 촬영 밸런스 — 최근 30일 태그 기준 아이별 사진 수. 적게 찍힌 아이가 위로 온다.
 * 세거나 기억할 필요 없이, 오늘 누구를 더 담을지 여기서 정한다.
 */
function CoverageCard() {
  const { data, isPending } = useQuery({
    queryKey: ['albums', 'coverage'],
    queryFn: () => apiData<{ days: number; children: CoverageRow[] }>('/albums/coverage'),
  });

  if (isPending) return <Spinner />;
  const rows = data?.children ?? [];
  if (rows.length === 0) return null;

  const max = Math.max(1, ...rows.map((row) => row.count));

  return (
    <Card>
      <p className="mb-0.5 flex items-center gap-1.5 font-semibold">
        <Scale size={16} className="text-primary-strong" /> 촬영 밸런스
      </p>
      <p className="mb-2 text-sm text-ink-sub">
        최근 {data?.days}일, 앨범에 태그된 사진 수예요. 위에 있는 아이부터 더 담아 주세요.
      </p>
      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={row.id} className="flex items-center gap-2">
            <span className="w-16 shrink-0 truncate text-sm font-medium">{row.name}</span>
            <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-bg">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.round((row.count / max) * 100)}%` }}
              />
            </div>
            <span className="tnum w-8 shrink-0 text-right text-sm">{row.count}장</span>
            <span className="w-14 shrink-0 text-right text-sm text-ink-sub">
              {row.lastPhotoAt ? formatDate(row.lastPhotoAt) : '아직'}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
